import { Fragment, useState } from "react";
import { Box, Button, createStyles, Flex, getStylesRef, Loader, Menu, Table, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconArrowRight, IconBan, IconDots, IconEdit, IconEye, IconInbox, IconRocket, IconTrash } from "@tabler/icons-react";
import RecordNotFound from "../RecordNotFound";
import { reorderBacklogRequest } from "../../services/serviceRequests";

const useStyles = createStyles(() => ({
  wrap: {
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    overflow: "hidden",
  },
  scroll: { overflowX: "auto" },
  table: {
    minWidth: 860,
    backgroundColor: "var(--app-surface) !important",
    fontFamily: "Inter !important",
    "& thead tr": { background: "var(--app-surface-2) !important" },
    "& thead th": {
      color: "var(--app-text) !important",
      fontFamily: "Inter !important",
      fontSize: "12.5px !important",
      fontWeight: "600 !important",
      padding: "14px 16px !important",
      textAlign: "left",
      whiteSpace: "nowrap",
      borderBottom: "1px solid var(--app-border) !important",
    },
    "& tbody td": {
      fontFamily: "Inter !important",
      padding: "14px 16px !important",
      borderBottom: "1px solid var(--app-border) !important",
      borderRight: "none !important",
      whiteSpace: "nowrap",
    },
    "& tbody tr.req-row:hover": { backgroundColor: "var(--app-surface-2) !important", cursor: "pointer" },
    [`& tbody tr.req-row:hover .${getStylesRef("rowGrip")}`]: { opacity: 1 },
    "& tbody tr.req-row.sel": { backgroundColor: "var(--app-tint-amber) !important" },
    "& tbody tr:last-child td": { borderBottom: "none !important" },
  },
  rowSection: {
    "& td": {
      background: "var(--app-surface-2) !important",
      padding: "9px 16px !important",
      borderTop: "1px solid var(--app-border) !important",
      borderBottom: "1px solid var(--app-border) !important",
    },
  },
  rowSectionSpaced: {
    "& td": { borderTop: "28px solid var(--app-bg) !important" },
  },
  // Dropping on the stage header sends the row to the top of that stage.
  rowSectionDragOver: {
    "& td": { background: "var(--app-tint-amber) !important", boxShadow: "inset 0 0 0 1px var(--app-primary)" },
  },
  rowDragging: { opacity: 0.4 },
  // The insert point reads as a line on the edge of the row it would land
  // against, above or below depending on which half the pointer is in.
  dropBefore: { "& td": { boxShadow: "inset 0 2px 0 0 var(--app-primary)" } },
  dropAfter: { "& td": { boxShadow: "inset 0 -2px 0 0 var(--app-primary)" } },
  rsTitle: { fontSize: 11.5, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--app-text)" },
  rsCount: {
    marginLeft: 8,
    fontSize: 11,
    fontWeight: 700,
    color: "var(--app-text-muted)",
    background: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    borderRadius: 999,
    padding: "1px 8px",
  },
  rowEmpty: { textAlign: "center", color: "var(--app-text-muted)", fontSize: 12.5, padding: "16px !important", cursor: "default" },
  // RecordNotFound draws its artwork inside a fixed 30vh box, which is shorter
  // than the image itself at most viewport heights — and `wrap` clips the
  // overflow. Let the artwork size itself instead of being cropped.
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "24px 16px 40px",
    "& > div": { height: "auto !important", paddingTop: "0 !important" },
    "& img": { maxWidth: 300, width: "100%", height: "auto" },
  },
  emptyText: { color: "var(--app-text-muted)", fontFamily: "Inter", fontSize: 15, textAlign: "center" },
  rowGrip: {
    ref: getStylesRef("rowGrip"),
    color: "var(--app-text-muted)",
    fontSize: 12,
    letterSpacing: -1,
    marginRight: 8,
    cursor: "grab",
    opacity: 0,
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    padding: "4px 12px",
    borderRadius: 999,
    border: "1px solid var(--app-border)",
    background: "var(--app-surface-2)",
    color: "var(--app-text)",
    whiteSpace: "nowrap",
  },
  prio: {
    fontSize: 12,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    "&::before": { content: '""', width: 7, height: 7, borderRadius: 999, background: "currentColor" },
  },
  prioUrgent: { color: "var(--app-ink-red)" },
  prioHigh: { color: "#C2570A" },
  prioMedium: { color: "var(--app-ink-amber)" },
  prioLow: { color: "var(--app-text-muted)" },
  kebabCol: { width: 60 },
  assignedYou: {
    marginLeft: 8,
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".04em",
    color: "var(--app-ink-amber)",
    background: "var(--app-tint-amber)",
    border: "1px solid var(--app-primary)",
    borderRadius: 999,
    padding: "2px 8px",
  },
  kebabBtn: {
    width: 30,
    height: 30,
    padding: 0,
    border: "none",
    borderRadius: 7,
    background: "none",
    color: "var(--app-text-muted)",
    display: "inline-grid",
    placeItems: "center",
    "&:hover": { background: "var(--app-surface-2)", color: "var(--app-text)" },
  },
  dropdown: { borderRadius: 10, boxShadow: "0 8px 24px rgba(16,24,40,.14)", padding: 6 },
  menuItem: {
    borderRadius: 7,
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 500,
    padding: "8px 10px",
    color: "var(--app-text)",
    "& .mantine-Menu-itemIcon": { color: "var(--app-text-muted)" },
    "&:hover": { backgroundColor: "var(--app-surface-2)" },
    "&[data-color='red']": { color: "var(--app-ink-red)" },
    "&[data-color='red'] .mantine-Menu-itemIcon": { color: "var(--app-ink-red)" },
    "&[data-color='red']:hover": { backgroundColor: "var(--app-tint-red)" },
  },
  menuDivider: { borderTopColor: "var(--app-border)", margin: "5px 4px" },
  pagination: {
    padding: "10px 16px",
    borderTop: "1px solid var(--app-border)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  },
  pageBtns: { display: "flex", gap: 6 },
  pageBtn: {
    minWidth: 34,
    height: 34,
    padding: "0 10px",
    border: "1px solid var(--app-border)",
    background: "var(--app-surface)",
    borderRadius: 8,
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 600,
    color: "var(--app-text)",
    cursor: "pointer",
    "&:hover": { background: "var(--app-surface-2)" },
    "&:disabled": { opacity: 0.45, cursor: "not-allowed", background: "var(--app-surface)" },
  },
  pageBtnActive: {
    background: "var(--app-primary)",
    borderColor: "var(--app-primary)",
    color: "var(--app-on-primary)",
    "&:hover": { background: "var(--app-primary)" },
  },

  // ── phone layout ──────────────────────────────────────────────────────────
  // The table is 8 columns wide with a hard 860px floor, so on a phone it can
  // only ever be a sideways scroll where the title — the one column that
  // identifies the row — is the first thing to leave the screen. Below 768px
  // each request becomes a card instead: id + status on top, the title as the
  // headline, then type/facility as chips and the two people underneath.
  // Same data, same row actions, no horizontal scroll.
  mList: { display: "flex", flexDirection: "column", gap: 10, padding: 12 },
  mSection: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "8px 12px",
    margin: "0 -12px",
    background: "var(--app-surface-2)",
    borderTop: "1px solid var(--app-border)",
    borderBottom: "1px solid var(--app-border)",
  },
  // The first stage header sits flush against the card's own top edge.
  mSectionFirst: { marginTop: -12, borderTop: 0 },
  mSectionSpaced: { marginTop: 6 },
  mCard: {
    border: "1px solid var(--app-border)",
    borderRadius: 10,
    background: "var(--app-surface)",
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    cursor: "pointer",
    "&:active": { background: "var(--app-surface-2)" },
  },
  mCardTop: { display: "flex", alignItems: "center", gap: 8 },
  mCardId: { fontSize: 12, fontWeight: 600, color: "var(--app-text-muted)", fontVariantNumeric: "tabular-nums" },
  // Pushes the kebab to the right edge whatever the status pill's width is.
  mCardTopRight: { marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 },
  mCardTitle: { fontSize: 14, fontWeight: 600, color: "var(--app-text)", lineHeight: 1.35 },
  mCardMeta: { display: "flex", flexWrap: "wrap", gap: 6 },
  mChip: {
    fontSize: 11,
    fontWeight: 600,
    color: "var(--app-text-muted)",
    background: "var(--app-surface-2)",
    border: "1px solid var(--app-border)",
    borderRadius: 6,
    padding: "2px 8px",
  },
  mCardFoot: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px 16px",
    paddingTop: 8,
    borderTop: "1px solid var(--app-border-soft, var(--app-border))",
  },
  mCardFootItem: { fontSize: 12, color: "var(--app-text)", minWidth: 0 },
  mCardFootKey: { fontSize: 10, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--app-text-muted)", display: "block" },
  mEmpty: { fontSize: 12.5, color: "var(--app-text-muted)", textAlign: "center", padding: "14px 8px" },
}));

// mockup's real backlog list only ever shows the "ready" and "backlog"
// stages — every other status lives on the board only.
const BACKLOG_STAGES = [
  { key: "ready", label: "Ready for Work" },
  { key: "backlog", label: "Backlog" },
];

export const STATUS_LABELS = {
  backlog: "Backlog",
  ready: "Ready for Work",
  in_progress: "In Review",
  awaiting_approval: "Awaiting Approval",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

// Requester self-cancel is only ever valid pre-review — matches the backend's
// own guard (409 outside backlog/ready).
const CANCELLABLE_STATUSES = ["backlog", "ready"];

// Reference fields (template/facility/submittedBy/assignedTo) come back
// either as a bare id string or a populated { id, name, ... } object —
// display whichever we got rather than assuming one shape.
const displayRef = (val) => {
  if (!val) return "—";
  if (typeof val === "string") return val;
  return val.name || val.title || [val.firstName, val.lastName].filter(Boolean).join(" ") || val.id || "—";
};

// Only ever relevant in the "Assigned to you" (mineOnly) view — the normal
// Backlog/Ready sections never contain an awaiting_approval row, since that
// stage lives on the board (see BACKLOG_STAGES above).
const signOffLabel = (r) => {
  if (r.status !== "awaiting_approval") return null;
  const approvedCount = (r.approvals || []).filter((a) => a.decision === "approved").length;
  const remaining = (r.quorumRequired || 1) - approvedCount;
  return remaining > 0 ? `${remaining} more sign-off` : null;
};

const ServiceRequestsTable = ({
  requests,
  loading,
  search,
  mineOnly,
  boardColumns,
  isAdmin,
  currentUserId,
  allowRequesterCancel,
  onView,
  onEdit,
  onMove,
  onDelete,
  onCancel,
  page = 1,
  totalPages = 1,
  onPageChange,
  // How tall the remaining viewport actually is, measured by the page
  // (ServiceRequests/index.jsx) rather than guessed here — a short page (few
  // rows) stretches the card down to that instead of leaving page background
  // showing below it.
  minHeight,
}) => {
  const { classes, cx } = useStyles();
  // Below this the row is rendered as a card (see mList above). Reordering by
  // drag goes with it: HTML5 drag-and-drop doesn't fire from touch at all, so
  // the affordance would be a dead grip on exactly the devices that get the
  // card layout — the kebab's "Move to …" items stay the way to restage there.
  // `getInitialValueInEffect: false` reads matchMedia on the first render
  // instead of after mount — the default would paint the 860px table for one
  // frame on every phone before swapping to cards.
  const isMobile = useMediaQuery("(max-width: 768px)", undefined, { getInitialValueInEffect: false });
  const filtered = requests || [];
  const [dragId, setDragId] = useState(null);
  // Where the row would land: against another row (above/below it) or at the
  // top of a stage, when the stage header itself is the target.
  const [dropMark, setDropMark] = useState(null); // { rowId, after } | { stage }

  // The backlog list only ever moves a card between the backlog and ready
  // stages — not to every non-terminal column the kanban board's card menu
  // would offer (which would include the approval gate). Those two stages
  // are, by definition, the first two non-gate/non-terminal columns by
  // order — the same positions serviceRequest.service.js's columnStatus()
  // derives 'backlog'/'ready' from.
  const backlogListTargets = (boardColumns || [])
    .filter((c) => !c.isApprovalGate && !c.isTerminal)
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .slice(0, 2);
  const columnForStage = { backlog: backlogListTargets[0], ready: backlogListTargets[1] };
  const moveTargets = (row) => backlogListTargets.filter((c) => c.key !== row.columnKey);
  const refId = (val) => (typeof val === "string" ? val : val?.id || val?._id);

  const dragRow = dragId ? filtered.find((r) => r.id === dragId) : null;

  const startRowDrag = (row) => (e) => {
    // Firefox refuses to start a drag at all unless dataTransfer carries
    // something; the id also lets the drop resolve the row.
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", row.id);
    setDragId(row.id);
  };
  const endRowDrag = () => { setDragId(null); setDropMark(null); };

  // Which half of the row the pointer sits in decides whether the row lands
  // above or below it — the same test the mockup's row drag uses.
  const dropsAfter = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    return e.clientY > r.top + r.height / 2;
  };
  const overRow = (row) => (e) => {
    if (!dragRow || row.id === dragId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const after = dropsAfter(e);
    setDropMark((m) => (m && m.rowId === row.id && m.after === after ? m : { rowId: row.id, after }));
  };
  const overStage = (stageKey) => (e) => {
    if (!dragRow) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropMark((m) => (m && m.stage === stageKey ? m : { stage: stageKey }));
  };

  // A drop carries two things: the stage the row now belongs to (persisted as
  // a column move) and where it sits within it (client-side — see
  // reorderBacklogRequest).
  const applyDrop = (stageKey, beforeId) => {
    const row = dragRow;
    endRowDrag();
    if (!row) return;
    const targetColumn = columnForStage[stageKey];
    if (targetColumn && row.status !== stageKey) onMove(row, targetColumn);
    reorderBacklogRequest(row.id, beforeId);
  };
  const dropOnRow = (row) => (e) => {
    if (!dragRow || row.id === dragId) return;
    e.preventDefault();
    const after = dropsAfter(e);
    // Landing "after" a row means landing before whatever follows it in that
    // stage; nothing following means the end of the list.
    const stageRows = filtered.filter((r) => r.status === row.status && r.id !== dragId);
    const i = stageRows.findIndex((r) => r.id === row.id);
    applyDrop(row.status, after ? stageRows[i + 1]?.id || null : row.id);
  };
  const dropOnStage = (stageKey) => (e) => {
    if (!dragRow) return;
    e.preventDefault();
    const first = filtered.find((r) => r.status === stageKey && r.id !== dragId);
    applyDrop(stageKey, first?.id || null);
  };

  const headerRow = (
    <tr>
      <th>ID</th><th>Request</th><th>Type</th><th>Facility</th><th>Submitted By</th><th>Assigned To</th><th>Status</th><th className={classes.kebabCol} />
    </tr>
  );

  // Shared by the table row's last cell and the card's top-right corner — the
  // same actions either way.
  const rowMenu = (r) => (
    <Menu shadow="md" width={190} withinPortal position="bottom-end" classNames={{ dropdown: classes.dropdown, item: classes.menuItem, divider: classes.menuDivider }}>
      <Menu.Target>
        <Button variant="unstyled" className={classes.kebabBtn} aria-label="Row actions"><IconDots size={16} /></Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item icon={<IconEye size={15} />} onClick={() => onView(r)}>View Details</Menu.Item>
        <Menu.Item icon={<IconEdit size={15} />} onClick={() => onEdit(r)}>Edit</Menu.Item>
        {moveTargets(r).map((c) => (
          <Menu.Item key={c.key} icon={<IconArrowRight size={15} />} onClick={() => onMove(r, c)}>Move to {c.label}</Menu.Item>
        ))}
        {allowRequesterCancel && currentUserId && refId(r.submittedBy) === currentUserId && CANCELLABLE_STATUSES.includes(r.status) && (
          <Menu.Item color="red" icon={<IconBan size={15} />} onClick={() => onCancel(r)}>Cancel Request</Menu.Item>
        )}
        {isAdmin && (
          <>
            <Menu.Divider />
            <Menu.Item color="red" icon={<IconTrash size={15} />} onClick={() => onDelete(r)}>Delete</Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );

  const renderCard = (r) => (
    <Box key={r.id} className={classes.mCard} onClick={() => onView(r)}>
      <Box className={classes.mCardTop}>
        <Text className={classes.mCardId}>{r.requestNumber || r.id}</Text>
        <Box className={classes.mCardTopRight}>
          <span className={classes.statusPill}>{STATUS_LABELS[r.status] || r.status}</span>
          <Box onClick={(e) => e.stopPropagation()}>{rowMenu(r)}</Box>
        </Box>
      </Box>
      <Text className={classes.mCardTitle}>
        {r.title}
        {currentUserId && refId(r.assignedTo) === currentUserId && <span className={classes.assignedYou}>You</span>}
        {signOffLabel(r) && <span className={classes.assignedYou}>{signOffLabel(r)}</span>}
      </Text>
      <Box className={classes.mCardMeta}>
        <span className={classes.mChip}>{displayRef(r.template)}</span>
        <span className={classes.mChip}>{displayRef(r.facility)}</span>
      </Box>
      <Box className={classes.mCardFoot}>
        <Text className={classes.mCardFootItem}>
          <span className={classes.mCardFootKey}>Submitted by</span>
          {displayRef(r.submittedBy)}
        </Text>
        <Text className={classes.mCardFootItem}>
          <span className={classes.mCardFootKey}>Assigned to</span>
          {r.assignedTo ? displayRef(r.assignedTo) : <Text span c="var(--app-text-muted)">Unassigned</Text>}
        </Text>
      </Box>
    </Box>
  );

  const renderCardSection = (label, Icon, count, { first, spaced } = {}) => (
    <Box className={cx(classes.mSection, first && classes.mSectionFirst, spaced && classes.mSectionSpaced)}>
      <span className={classes.rsIcon}><Icon size={14} /></span>
      <span className={classes.rsTitle}>{label}</span>
      <span className={classes.rsCount}>{count}</span>
    </Box>
  );

  const renderRow = (r) => (
    <tr
      key={r.id}
      // The whole row is the drag handle, as in the mockup — the grip is the
      // affordance that says so, not the only place it can be grabbed. My
      // Queue is a flat cross-stage list with no sections to drop into, so it
      // stays undraggable.
      {...(mineOnly ? {} : {
        draggable: true,
        onDragStart: startRowDrag(r),
        onDragEnd: endRowDrag,
        onDragOver: overRow(r),
        onDrop: dropOnRow(r),
      })}
      className={cx(
        "req-row",
        dragId === r.id && classes.rowDragging,
        dropMark?.rowId === r.id && (dropMark.after ? classes.dropAfter : classes.dropBefore)
      )}
      onClick={() => onView(r)}
    >
      <td>
        <span className={classes.rowGrip} title="Drag to move">⠿</span>
        <Text fz={12} fw={600} c="var(--app-text-muted)" ff="Inter" span>{r.requestNumber || r.id}</Text>
      </td>
      <td>
        <Text fz={13.5} fw={600} c="var(--app-text)" ff="Inter">
          {r.title}
        </Text>
      </td>
      <td><Text fz={12} c="var(--app-text-muted)" ff="Inter">{displayRef(r.template)}</Text></td>
      <td><Text fz={13.5} ff="Inter">{displayRef(r.facility)}</Text></td>
      <td><Text fz={13.5} ff="Inter">{displayRef(r.submittedBy)}</Text></td>
      <td><Text fz={13.5} ff="Inter">{r.assignedTo ? displayRef(r.assignedTo) : <Text span c="var(--app-text-muted)">Unassigned</Text>}</Text></td>
      <td>
        <span className={classes.statusPill}>{STATUS_LABELS[r.status] || r.status}</span>
        {signOffLabel(r) && <span className={classes.assignedYou}>{signOffLabel(r)}</span>}
      </td>
      <td onClick={(e) => e.stopPropagation()}>{rowMenu(r)}</td>
    </tr>
  );

  // Nothing to show yet and a fetch in flight is a load, not an empty list —
  // "no records" would be a lie until the first response lands. A refetch
  // that still has rows on screen keeps them, so typing in the search box
  // doesn't flash the table away between keystrokes.
  if (!filtered.length) {
    return (
      <Box className={classes.wrap} style={{ minHeight }}>
        {/* The header row exists to label columns — on the card layout there
            are none, and rendering it anyway would put a sideways scrollbar
            on an otherwise empty panel. */}
        {!isMobile && (
          <Box className={classes.scroll}>
            <Table className={classes.table} fontSize="sm" verticalSpacing="md">
              <thead>{headerRow}</thead>
            </Table>
          </Box>
        )}
        {loading
          ? <Flex justify="center" py={48}><Loader color="var(--app-primary)" /></Flex>
          : (
            <Box className={classes.empty}>
              <RecordNotFound />
              <Text className={classes.emptyText}>
                {search
                  ? `No requests match “${search}”`
                  : mineOnly
                    ? "Nothing is assigned to you right now"
                    : "No service requests yet"}
              </Text>
            </Box>
          )}
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Box className={classes.wrap} style={{ minHeight }}>
        <Box className={classes.mList}>
          {mineOnly ? (
            <>
              {renderCardSection("Assigned to you", IconInbox, filtered.length, { first: true })}
              {filtered.map(renderCard)}
            </>
          ) : (
            BACKLOG_STAGES.map((stage, i) => {
              const items = filtered.filter((r) => r.status === stage.key);
              return (
                <Fragment key={stage.key}>
                  {renderCardSection(stage.label, stage.icon, items.length, { first: i === 0, spaced: i > 0 })}
                  {items.length
                    ? items.map(renderCard)
                    : <Text className={classes.mEmpty}>No requests in this stage</Text>}
                </Fragment>
              );
            })
          )}
        </Box>
        <Box className={classes.pagination}>
          <Text fz={13} c="var(--app-text-muted)">
            Showing <Text span fw={700} c="var(--app-text)">{filtered.length}</Text> {mineOnly ? "ticket" : "backlog"}{filtered.length === 1 ? "" : "s"}
          </Text>
          <Box className={classes.pageBtns}>
            <button type="button" className={classes.pageBtn} disabled={page <= 1} onClick={() => onPageChange?.(page - 1)}>‹</button>
            <button type="button" className={cx(classes.pageBtn, classes.pageBtnActive)}>{page}</button>
            <button type="button" className={classes.pageBtn} disabled={page >= totalPages} onClick={() => onPageChange?.(page + 1)}>›</button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.wrap} style={{ minHeight }}>
      <Box className={classes.scroll}>
        <Table className={classes.table} fontSize="sm" verticalSpacing="md">
          <thead>{headerRow}</thead>
          <tbody>
            {mineOnly ? (
              <>
                <tr className={classes.rowSection}>
                  <td colSpan={8}>
                    <span className={classes.rsTitle}>Assigned to you</span>
                    <span className={classes.rsCount}>{filtered.length}</span>
                  </td>
                </tr>
                {filtered.map(renderRow)}
              </>
            ) : (
              BACKLOG_STAGES.map((stage, i) => {
                const items = filtered.filter((r) => r.status === stage.key);
                return (
                  <Fragment key={stage.key}>
                    <tr
                      className={cx(classes.rowSection, i > 0 && classes.rowSectionSpaced, dropMark?.stage === stage.key && classes.rowSectionDragOver)}
                      onDragOver={overStage(stage.key)}
                      onDrop={dropOnStage(stage.key)}
                    >
                      <td colSpan={8}>
                        <span className={classes.rsTitle}>{stage.label}</span>
                        <span className={classes.rsCount}>{items.length}</span>
                      </td>
                    </tr>
                    {items.length
                      ? items.map(renderRow)
                      : (
                        <tr
                          className={cx(dropMark?.stage === stage.key && classes.rowSectionDragOver)}
                          onDragOver={overStage(stage.key)}
                          onDrop={dropOnStage(stage.key)}
                        >
                          <td colSpan={8} className={classes.rowEmpty}>
                            {dragRow ? `Drop here to move to ${stage.label}` : "No requests in this stage"}
                          </td>
                        </tr>
                      )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </Table>
      </Box>
      <Box className={classes.pagination}>
        <Text fz={13} c="var(--app-text-muted)">
          Showing <Text span fw={700} c="var(--app-text)">{filtered.length}</Text> {mineOnly ? "ticket" : "backlog"}{filtered.length === 1 ? "" : "s"}
        </Text>
        <Box className={classes.pageBtns}>
          <button
            type="button"
            className={classes.pageBtn}
            disabled={page <= 1}
            onClick={() => onPageChange?.(page - 1)}
          >
            ‹
          </button>
          <button type="button" className={cx(classes.pageBtn, classes.pageBtnActive)}>{page}</button>
          <button
            type="button"
            className={classes.pageBtn}
            disabled={page >= totalPages}
            onClick={() => onPageChange?.(page + 1)}
          >
            ›
          </button>
        </Box>
      </Box>
    </Box>
  );
};

export default ServiceRequestsTable;
